import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';


import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  AlertCircle, 
  Loader, 
  Home, 
  Clock,
  CheckCircle2,
  Info,
  AlertTriangle
} from 'lucide-react';

export const CallbackPage = () => {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    user,
  } = useAuth0();

  const navigate = useNavigate();
  const [processingStatus, setProcessingStatus] = useState('pending');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    // Debug logging
    console.log('Auth State:', {
      isAuthenticated,
      isLoading,
      hasUser: !!user,
      userEmail: user?.email,
      emailVerified: user?.email_verified,
      urlParams: new URLSearchParams(window.location.search).toString()
    });
  }, [isAuthenticated, isLoading, user]);

  const getPageContent = () => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    switch (error) {
      case 'rate_limit_reached':
        return {
          icon: <Clock className="text-yellow-500" size={40} strokeWidth={1.5} />,
          title: 'Please Wait',
          severity: 'warning',
          message: errorDescription || 'Please wait before requesting another verification email.',
          showSpamNote: false
        };
      
      case 'verification_error':
        return {
          icon: <AlertTriangle className="text-red-500" size={40} strokeWidth={1.5} />,
          title: 'Error',
          severity: 'error',
          message: errorDescription || 'Failed to send verification email. Please try again later.',
          showSpamNote: false
        };
      
      case 'verification_needed':
      default:
        return {
          icon: <CheckCircle2 className="text-blue-500" size={40} strokeWidth={1.5} />,
          title: 'One Last Step!',
          severity: 'info',
          message: errorDescription || 'Please verify your email address to continue.',
          showSpamNote: true
        };
    }
  };

  // Loading state
  if (isLoading || processingStatus === 'processing') {
    return (
      <Container maxWidth="sm">
        <Box sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Paper 
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              width: '100%',
              maxWidth: 400,
              borderRadius: 2
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Loader className="animate-spin" size={40} strokeWidth={1.5} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Setting Up Your Account
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we complete the setup process...
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  const content = getPageContent();

  return (
    <Container maxWidth="sm">
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            maxWidth: 400,
            borderRadius: 2
          }}
        >
          <Box sx={{ mb: 2 }}>
            {content.icon}
          </Box>
          
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {content.title}
          </Typography>

          <Alert 
            severity={content.severity}
            sx={{ mb: 3 }}
          >
            {content.message}
          </Alert>

          {content.showSpamNote && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              Can't find the email? Check your spam folder
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={() => navigate('/')}
            startIcon={<Home size={20} />}
            fullWidth
            sx={{ 
              py: 1.5,
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Return to Home
          </Button>
        </Paper>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
    </Container>
  );
};

export default CallbackPage;